/**
 * ADR-123 — "My clusters / All clusters" scope filter for the Cluster tree.
 *
 * This module is a VIEW FILTER, not authorization. Nothing here restricts what a
 * user may schedule: `GET /clusters` stays unscoped and the schedule endpoints keep
 * accepting any valid clusterId from any caller. Do not cite it as a permission gate.
 * Real cluster-scoped authorization remains owed by the phase-2 ADR (see ADR-003).
 *
 * Pure: no React, no network, no auth. Everything is a function of data the caller
 * already holds — the `GET /clusters` payload and the `clusters` array from `GET /me`.
 */

import { scopeTree } from './treeScope';

export const CLUSTER_SCOPE = Object.freeze({
  MINE: 'mine',
  ALL: 'all',
});

/**
 * The ids an employee is directly associated with, per `GET /me`.
 *
 * The payload shape is `clusters: [{ id }]` — id only, no name — so callers must
 * match by id against the full `getClusters()` response to resolve anything else.
 *
 * Semantics (radar-api `employeeTransformer` + `hierarchyService`): clusters of
 * managed Business Units, plus clusters of managed teams' BUs, plus the employee's
 * own `clusterId`. It is a computed reach set, not a persisted membership — there is
 * no Employee<->Cluster join table.
 *
 * ADR-123 Amendment 5 (ADR-125 Decision 9): also unions `memberOfBusinessUnits[].clusterId`.
 * Managed-BU clusters are already inside `clusters`, so only the member side is added.
 *
 * `managedClusters` is deliberately NOT unioned in; see ADR-123 Decision 2.
 *
 * @param {{ clusters?: Array<{ id?: string }>, memberOfBusinessUnits?: Array<{ clusterId?: string }> }} [employee] the `/me` payload
 * @returns {Set<string>} seed ids, possibly empty
 */
export const resolveMyClusterIds = (employee) =>
  new Set(
    [
      ...(employee?.clusters ?? []).map((c) => c?.id),
      // ADR-123 Amendment 5 / ADR-125 Decision 9: plus the clusters of the Business Units
      // the employee (or their Manager-of-Record reports) is allocated in, so every Cluster
      // in which the Select Business Unit step has a seed is visible under "Mine" here.
      ...(employee?.memberOfBusinessUnits ?? []).map((bu) => bu?.clusterId),
    ].filter(Boolean)
  );

/**
 * Narrow a flat cluster list to a scoped, renderable tree.
 *
 * Three passes (ADR-123 Decision 3):
 *   1. seed             — clusters whose id is in `myIds`
 *   2. descendant expand — plus every cluster beneath a seed
 *   3. ancestor closure  — plus every ancestor of (1)+(2), up to the root
 *
 * Passes 1+2 are SELECTABLE. Pass 3 is context only: drawn so the tree stays
 * connected and keeps exactly one parentless root, but not selectable.
 *
 * Ancestor closure is what makes this safe to hand to `buildHierarchy`, which does
 * `clusters.find(c => !c.parentClusterId)` and returns null (rendering an empty box
 * with no error) for a rootless set. A naive `.filter()` over a reach set of two
 * sibling mid-tree ids produces exactly that rootless set.
 *
 * Returned cluster objects have `childClusters` pruned to visible ids, because
 * `buildHierarchy` recurses through `childClusters` and would dereference a pruned
 * child as `undefined`.
 *
 * Both walks are cycle-guarded: `Cluster.parentClusterId` has no acyclicity
 * constraint in the database, and an infinite loop in a render path is worse than
 * an odd-looking tree.
 *
 * @param {Array<object>} allClusters the full `getClusters()` payload
 * @param {Set<string>|null|undefined} myIds seed ids from `resolveMyClusterIds`
 * @returns {{ clusters: Array<object>, selectableIds: Set<string>|null }}
 *          `selectableIds === null` means "no scoping applied — everything selectable"
 */
export const scopeClusters = (allClusters, myIds) => {
  const list = Array.isArray(allClusters) ? allClusters : [];

  // ADR-125: the three passes live in the shared `treeScope` core. `selectableIds === null`
  // covers both "no seed set" and "every seed id was stale" — in either case fall back to
  // unscoped rather than handing the tree an empty array it would render as a blank box.
  const { visibleIds, selectableIds } = scopeTree(
    list,
    myIds,
    (c) => c?.id,
    (c) => c?.parentClusterId
  );
  if (!selectableIds) {
    return { clusters: list, selectableIds: null };
  }

  const clusters = list
    .filter((c) => visibleIds.has(c.id))
    .map((c) =>
      Array.isArray(c.childClusters)
        ? { ...c, childClusters: c.childClusters.filter((child) => visibleIds.has(child?.id)) }
        : c
    );

  return { clusters, selectableIds };
};
